import type { RevitFamily } from '../../../shared/api/revit/types';
import type { QuickFillDataScope } from '../../../shared/api/grpc/quickFillClient';
import { perf } from '../../../shared/lib/perfLogger';
import {
  scopeQuickFillSnapshot,
  type QuickFillDataSnapshot,
  type QuickFillPipeElement,
} from './quickFillDataCache';

const SampleLimit = 6;

function collectSampleParameterNames(pushNames: (names: Set<string>) => void): string[] {
  const names = new Set<string>();
  pushNames(names);
  return Array.from(names);
}

function collectSampleFamilyParameterNames(families: RevitFamily[]): string[] {
  return collectSampleParameterNames((names) => {
    families.forEach((family) => family.types.forEach((type) => {
      type.parameterValues?.forEach((parameterValue) => {
        if (parameterValue.parameterName && names.size < SampleLimit) {
          names.add(parameterValue.parameterName);
        }
      });
    }));
  });
}

function collectSampleElementParameterNames(elements: QuickFillPipeElement[]): string[] {
  return collectSampleParameterNames((names) => {
    elements.forEach((element) => element.parameterValues?.forEach((parameterValue) => {
      if (parameterValue.parameterName && names.size < SampleLimit) {
        names.add(parameterValue.parameterName);
      }
    }));
  });
}

export function mapQuickFillGrpcResultToSnapshot(
  result: { success: boolean; families?: any[]; pipeElements?: any[]; availableParameters?: any[]; error?: string },
  dataScope?: QuickFillDataScope,
  parameterNames?: string[]
): QuickFillDataSnapshot {
  if (!result.success) throw new Error(result.error ?? 'Failed to load QuickFill data');

  return scopeQuickFillSnapshot({
    families: (result.families ?? []).map((family) => ({
      id: family.id,
      name: family.name,
      category: family.category,
      isEditable: family.isEditable,
      isSystemFamily: family.isSystemFamily,
      parameters: [],
      types: (family.types ?? []).map((type: any) => ({
        id: type.id,
        name: type.name,
        parameterValues: (type.parameterValues ?? []).map((parameter: any) => ({
          parameterId: parameter.parameterId,
          parameterName: parameter.parameterName,
          value: parameter.value,
          isReadOnly: parameter.isReadOnly,
          isInstance: parameter.isInstance,
        })),
      })),
    })),
    pipeElements: (result.pipeElements ?? []).map((element) => ({
      elementId: element.id,
      elementName: element.elementName,
      familyName: element.familyName,
      typeName: element.typeName,
      typeId: element.typeId,
      category: element.category,
      parameterValues: (element.parameterValues ?? []).map((parameter: any) => ({
        parameterId: parameter.parameterId,
        parameterName: parameter.parameterName,
        value: parameter.value,
        isReadOnly: parameter.isReadOnly,
        isInstance: parameter.isInstance,
      })),
    })),
    availableParameters: (result.availableParameters ?? []).map((parameter) => ({
      name: parameter.name,
      parameterType: parameter.parameterType || 'string',
      group: parameter.group || 'General',
      isInstance: parameter.isInstance === true ||
        String(parameter.parameterType || '').trim().toLowerCase() === 'instance',
    })),
  }, dataScope, parameterNames);
}

export function logQuickFillSnapshotSuccess(
  cacheKey: string,
  snapshot: QuickFillDataSnapshot
): void {
  perf.log('QF-Data', 'success', {
    cacheKey,
    families: snapshot.families.length,
    types: snapshot.families.reduce((sum, family) => sum + family.types.length, 0),
    elements: snapshot.pipeElements.length,
    availableParameters: snapshot.availableParameters.length,
    sampleFamilies: snapshot.families.slice(0, 4).map((family) => family.name),
    sampleFamilyParameterNames: collectSampleFamilyParameterNames(snapshot.families),
    sampleElementParameterNames: collectSampleElementParameterNames(snapshot.pipeElements),
  });
}
